export interface SuccessStory {
  id: string;
  creatorId: string;
  creatorName: string;
  creatorUsername: string;
  creatorAvatar?: string;
  creatorCategory: string;
  title: string;
  excerpt: string;
  story: string;
  quote: string;
  metrics: {
    patronCount: number;
    monthlyRevenue: number;
    postsCreated: number;
    yearsActive: number;
  };
  tags: string[];
  featuredImage?: string;
  publishedAt: Date;
  isFeatured: boolean;
}

// Mock success stories data
export const mockSuccessStories: SuccessStory[] = [
  {
    id: '1',
    creatorId: 'creator1',
    creatorName: 'Sarah Chen',
    creatorUsername: 'sarahartist',
    creatorAvatar: 'https://i.pravatar.cc/150?img=5',
    creatorCategory: 'Digital Art',
    title: 'From Hobbyist to Full-Time Artist',
    excerpt: 'How Sarah turned her passion for digital art into a thriving online business with 2,500+ patrons.',
    story: `When I started creating art in 2020, I never imagined I could make a living from it. I was working a 9-5 corporate job and painting in my spare time. After posting some artwork online and getting positive feedback, I decided to try this platform.

The first few months were slow - I had maybe 10 patrons. But I stayed consistent, posting tutorials twice a week and engaging with my community. By month 6, I hit 100 patrons. That's when things clicked.

I started offering exclusive content - behind-the-scenes videos, early access to new pieces, and personalized art critiques for higher tiers. My community grew organically through word of mouth.

Now, three years later, I'm a full-time artist with over 2,500 patrons. I've published two digital art courses, shipped physical prints to 30 countries, and most importantly, I wake up excited to create every single day.

The key? Consistency, authenticity, and truly caring about your community. Treat your patrons like partners in your creative journey, not just customers.`,
    quote: "This platform didn't just give me income - it gave me creative freedom and a supportive community.",
    metrics: {
      patronCount: 2547,
      monthlyRevenue: 12800,
      postsCreated: 432,
      yearsActive: 3,
    },
    tags: ['Art', 'Tutorial', 'Community'],
    featuredImage: 'https://picsum.photos/800/500?random=1',
    publishedAt: new Date('2024-01-15'),
    isFeatured: true,
  },
  {
    id: '2',
    creatorId: 'creator2',
    creatorName: 'Marcus Johnson',
    creatorUsername: 'musicmarcus',
    creatorAvatar: 'https://i.pravatar.cc/150?img=12',
    creatorCategory: 'Music',
    title: 'Independent Musician Finds His Audience',
    excerpt: 'Marcus built a sustainable music career with 1,800 patrons, releasing albums without a label.',
    story: `As an independent musician, getting discovered felt impossible. Record labels wanted me to change my sound, and streaming platforms paid pennies. I was about to give up music entirely.

Then I found this platform. Instead of trying to reach millions, I focused on connecting with true fans - people who genuinely loved my music and wanted to support me.

I started with acoustic performances live-streamed weekly, sharing my songwriting process, and offering early access to new tracks. My first album funded through patron support reached #3 on independent charts.

The best part? I own 100% of my music. No label dictating my creative direction. My patrons trust me to create authentically, and I deliver consistently.

Today, I have 1,800 monthly supporters generating enough income to produce albums, tour selectively, and maintain creative control. I've also connected with other musicians through this platform, leading to amazing collaborations.`,
    quote: "My patrons aren't just fans - they're partners who believe in my music before anyone else does.",
    metrics: {
      patronCount: 1823,
      monthlyRevenue: 8400,
      postsCreated: 267,
      yearsActive: 2.5,
    },
    tags: ['Music', 'Live Performance', 'Albums'],
    featuredImage: 'https://picsum.photos/800/500?random=2',
    publishedAt: new Date('2024-02-01'),
    isFeatured: true,
  },
  {
    id: '3',
    creatorId: 'creator3',
    creatorName: 'Dr. Emily Rodriguez',
    creatorUsername: 'scienceemily',
    creatorAvatar: 'https://i.pravatar.cc/150?img=9',
    creatorCategory: 'Education',
    title: 'Making Science Accessible to Everyone',
    excerpt: 'A PhD researcher uses the platform to teach complex science topics to 3,200+ curious minds.',
    story: `With a PhD in astrophysics, I spent years writing papers that only a handful of peers would read. I wanted to share my love of science with a broader audience, but academic publishing wasn't cutting it.

I started creating bite-sized science explainers - complex topics made digestible. From quantum mechanics to black holes, I broke down intimidating subjects into engaging content.

My approach: no gatekeeping. I made science fun, relatable, and accessible. Weekly Q&A sessions let patrons ask anything, no matter how "basic" the question seemed.

The response was overwhelming. Teachers use my content in classrooms. Parents watch with their kids. Curious adults dive into topics they thought were "too hard" for them.

Building this took time - consistent posting, listening to feedback, and constantly improving. But at 3,200 patrons, I'm reaching more people than I ever did in academia. And I'm funding my own research independently.`,
    quote: "Science belongs to everyone. This platform helped me tear down the ivory tower, one video at a time.",
    metrics: {
      patronCount: 3204,
      monthlyRevenue: 15600,
      postsCreated: 521,
      yearsActive: 4,
    },
    tags: ['Education', 'Science', 'Research'],
    featuredImage: 'https://picsum.photos/800/500?random=3',
    publishedAt: new Date('2024-01-20'),
    isFeatured: true,
  },
  {
    id: '4',
    creatorId: 'creator4',
    creatorName: 'Alex Turner',
    creatorUsername: 'gamedevalex',
    creatorAvatar: 'https://i.pravatar.cc/150?img=33',
    creatorCategory: 'Game Development',
    title: 'Indie Game Dev Goes Full-Time',
    excerpt: 'Alex quit his AAA studio job to create indie games funded by 950 passionate supporters.',
    story: `Working at a major game studio was my dream - until it wasn't. I was working 80-hour weeks on games I didn't care about, with no creative control. I wanted to make smaller, more meaningful games.

Starting this platform account was terrifying. Could I really sustain myself? I documented my entire game development process - from concept to code to release. Patrons got dev logs, alpha access, and input on game features.

My first game took 14 months to complete. Throughout development, my community supported me financially and emotionally. When I launched, they were my biggest advocates.

That game sold 50,000 copies in the first year. Revenue from sales plus ongoing patron support means I'm now working on game #3. Each project has been more ambitious than the last.

The freedom to experiment, fail, and learn has made me a better developer. And my patrons are invested in the journey, not just the destination.`,
    quote: "I traded job security for creative freedom, and my patrons made that leap possible.",
    metrics: {
      patronCount: 952,
      monthlyRevenue: 5800,
      postsCreated: 189,
      yearsActive: 2,
    },
    tags: ['Gaming', 'Development', 'Indie'],
    featuredImage: 'https://picsum.photos/800/500?random=4',
    publishedAt: new Date('2024-02-10'),
    isFeatured: false,
  },
  {
    id: '5',
    creatorId: 'creator5',
    creatorName: 'Priya Patel',
    creatorUsername: 'writingwithpriya',
    creatorAvatar: 'https://i.pravatar.cc/150?img=45',
    creatorCategory: 'Writing',
    title: 'Self-Published Author Builds Loyal Readership',
    excerpt: 'Priya published 3 novels with support from 1,400 readers who funded her writing journey.',
    story: `Traditional publishing rejected my manuscript 47 times. "Too niche," they said. "Not commercial enough." But I knew there were readers out there who would love my stories.

I started serializing my first novel chapter by chapter. Patrons got early access, bonus content, and behind-the-scenes looks at my writing process. Some even influenced plot points through polls.

As my patron base grew, I was able to invest in professional editing, cover design, and marketing. My first self-published novel sold 10,000 copies in year one - modest by bestseller standards, but life-changing for me.

Now on book three, I have 1,400 dedicated readers who fund my writing before a single book is sold. They're not just supporting my work - they're part of my creative community.

The traditional publishing route wasn't for me. This platform let me forge my own path.`,
    quote: "My 1,400 patrons are worth more than any publishing advance. They believe in my stories.",
    metrics: {
      patronCount: 1402,
      monthlyRevenue: 7200,
      postsCreated: 312,
      yearsActive: 3,
    },
    tags: ['Writing', 'Fiction', 'Publishing'],
    featuredImage: 'https://picsum.photos/800/500?random=5',
    publishedAt: new Date('2024-01-25'),
    isFeatured: false,
  },
  {
    id: '6',
    creatorId: 'creator6',
    creatorName: 'James Kim',
    creatorUsername: 'photographyjames',
    creatorAvatar: 'https://i.pravatar.cc/150?img=68',
    creatorCategory: 'Photography',
    title: 'Travel Photographer Funds Adventures',
    excerpt: 'James travels the world full-time, documenting his journeys with support from 780 patrons.',
    story: `I was stuck in a cubicle dreaming of travel photography. Everyone said "you can't make money from it." But I had to try.

I started small - weekend trips, local landscapes. I shared not just photos, but the stories behind them. How I planned shoots, dealt with weather, found unique perspectives.

Patrons loved the educational aspect. I offered Lightroom presets, location guides, and photography tips. Slowly, I built enough support to take a 3-month sabbatical.

That turned into full-time travel photography. I've now visited 35 countries, all funded by my 780 patrons. Each month, they get exclusive prints, desktop wallpapers, and tutorials.

The key was building genuine relationships. I respond to every comment, take patron suggestions for locations, and share both successes and failures.

Living my dream wouldn't be possible without this community.`,
    quote: "Every photo I take is possible because 780 people believed in my vision.",
    metrics: {
      patronCount: 782,
      monthlyRevenue: 4300,
      postsCreated: 445,
      yearsActive: 2,
    },
    tags: ['Photography', 'Travel', 'Education'],
    featuredImage: 'https://picsum.photos/800/500?random=6',
    publishedAt: new Date('2024-02-05'),
    isFeatured: false,
  },
];
